import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiDownload, FiZoomIn, FiZoomOut, FiMaximize, FiAlertCircle, FiImage, FiPaperclip } from 'react-icons/fi';
import es_ES from '@react-pdf-viewer/locales/lib/es_ES.json';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { parseEml, formatSize } from '../../libs/eml/emlParser';
import {
  SANDBOX,
  buildEmlDocument,
  createInlineUrls,
  resolveCidImages,
} from '../../libs/eml/emlFrame';

const PDF_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.js';
//Extensiones que se usan cuando el correo declara el adjunto como octet-stream
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'ico'];
const TEXT_EXTENSIONS = ['txt', 'csv', 'log', 'json', 'xml', 'md', 'ini', 'sql', 'yml', 'yaml'];
const HTML_EXTENSIONS = ['htm', 'html'];
const ZOOM_STEP = 25;
const ZOOM_MIN = 25;
const ZOOM_MAX = 500;

/**
 * Devuelve la extension en minusculas del nombre del adjunto
 * @param {string} filename nombre del adjunto
 * @returns {string} extension sin punto, cadena vacia si no la tiene
 */
const getExtension = (filename) => {
  const parts = String(filename || '').split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * Determina como se debe previsualizar un adjunto.
 * Se revisa primero el tipo declarado y luego la extension, porque muchos
 * clientes de correo envian los adjuntos como application/octet-stream.
 * @param {object} attachment adjunto del correo
 * @returns {string} tipo de previsualizacion soportada
 */
const resolveKind = (attachment) => {
  const type = String(attachment.contentType || '').toLowerCase();
  const extension = getExtension(attachment.filename);
  if (type.startsWith('image/') || IMAGE_EXTENSIONS.includes(extension)) {
    return 'image';
  }
  if (type === 'application/pdf' || extension === 'pdf') {
    return 'pdf';
  }
  if (type === 'message/rfc822' || extension === 'eml') {
    return 'eml';
  }
  if (type === 'text/html' || HTML_EXTENSIONS.includes(extension)) {
    return 'html';
  }
  if (
    type.startsWith('text/') ||
    type === 'application/json' ||
    type === 'application/xml' ||
    TEXT_EXTENSIONS.includes(extension)
  ) {
    return 'text';
  }
  return 'unsupported';
};

/**
 * Indica si un contenido apunta a recursos remotos
 * @param {string} html contenido a revisar
 * @returns {boolean} verdadero cuando hay referencias http
 */
const hasRemoteContent = (html) => /(src|url\()\s*=?\s*["'(]?https?:/i.test(String(html || ''));

/**
 * Indica si un adjunto se puede previsualizar dentro del visor
 * @param {object} attachment adjunto del correo
 * @returns {boolean} verdadero cuando hay previsualizacion disponible
 */
export const canPreview = (attachment) => resolveKind(attachment) !== 'unsupported';

/**
 * Previsualizacion de un adjunto del correo sin salir del visor
 * @param {object} props attachment, showDownload, onDownload y onClose
 */
function EmlPreview({ attachment, showDownload, onDownload, onClose }) {
  const kind = useMemo(() => resolveKind(attachment), [attachment]);
  const [zoom, setZoom] = useState(100);
  const [allowRemote, setAllowRemote] = useState(false);

  useEffect(() => {
    setZoom(100);
    setAllowRemote(false);
  }, [attachment]);

  //Url local de la imagen, solo se crea cuando el adjunto es una imagen
  const imageUrl = useMemo(() => {
    if (kind !== 'image') {
      return '';
    }
    return URL.createObjectURL(new Blob([attachment.bytes], { type: attachment.contentType }));
  }, [kind, attachment]);

  useEffect(() => {
    return () => {
      if (imageUrl !== '') {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  /**
   * Copia de los bytes del pdf.
   * pdf.js se queda con el buffer que recibe, asi que se le entrega una copia
   * para que la descarga del adjunto siga funcionando despues de verlo.
   */
  const pdfBytes = useMemo(
    () => (kind === 'pdf' ? new Uint8Array(attachment.bytes) : null),
    [kind, attachment]
  );

  const textContent = useMemo(() => {
    if (kind !== 'text' && kind !== 'html') {
      return '';
    }
    return new TextDecoder('utf-8', { fatal: false }).decode(attachment.bytes);
  }, [kind, attachment]);

  //Correo anidado ya interpretado, null cuando el archivo no se pudo leer
  const nestedEmail = useMemo(() => {
    if (kind !== 'eml') {
      return null;
    }
    try {
      return parseEml(attachment.bytes);
    } catch (error) {
      return null;
    }
  }, [kind, attachment]);

  const nestedUrls = useMemo(
    () => (nestedEmail ? createInlineUrls(nestedEmail.attachments) : {}),
    [nestedEmail]
  );

  useEffect(() => {
    return () => {
      Object.values(nestedUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [nestedUrls]);

  useEffect(() => {
    /**
     * Cierra la previsualizacion con la tecla escape
     * @param {KeyboardEvent} event evento de teclado
     * @returns {void}
     */
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const frameDocument = useMemo(() => {
    if (kind === 'html') {
      return buildEmlDocument({ html: textContent, allowRemote });
    }
    if (kind === 'eml' && nestedEmail !== null) {
      return buildEmlDocument({
        html: nestedEmail.html ? resolveCidImages(nestedEmail.html, nestedUrls) : null,
        text: nestedEmail.text,
        allowRemote,
      });
    }
    return '';
  }, [kind, textContent, nestedEmail, nestedUrls, allowRemote]);

  const remoteAvailable =
    (kind === 'html' && hasRemoteContent(textContent)) ||
    (kind === 'eml' && nestedEmail !== null && hasRemoteContent(nestedEmail.html));

  //Adjuntos visibles del correo anidado, se omiten las imagenes incrustadas
  const nestedAttachments = nestedEmail
    ? nestedEmail.attachments.filter((item) => !item.inline || item.contentId === '')
    : [];

  const imageStyle =
    zoom === 100 ? { maxWidth: '100%', maxHeight: '100%' } : { width: `${zoom}%`, maxWidth: 'none' };

  /**
   * Renderiza el contenido segun el tipo de adjunto
   * @returns {JSX.Element} cuerpo de la previsualizacion
   */
  const renderContent = () => {
    if (kind === 'image') {
      return (
        <div className="eml-preview-image-area">
          <img src={imageUrl} alt={attachment.filename} style={imageStyle} />
        </div>
      );
    }
    if (kind === 'pdf') {
      return (
        <Worker workerUrl={PDF_WORKER_URL}>
          <div className="eml-preview-pdf">
            <Viewer
              fileUrl={pdfBytes}
              defaultScale={SpecialZoomLevel.PageWidth}
              localization={es_ES}
              theme="light"
            />
          </div>
        </Worker>
      );
    }
    if (kind === 'text') {
      return <pre className="eml-preview-text">{textContent}</pre>;
    }
    if (kind === 'html') {
      return (
        <iframe
          className="eml-preview-frame"
          title={attachment.filename}
          sandbox={SANDBOX}
          srcDoc={frameDocument}
        />
      );
    }
    if (kind === 'eml') {
      if (nestedEmail === null) {
        return (
          <div className="eml-preview-empty">
            <FiAlertCircle size={28} />
            &nbsp;No se pudo leer el correo adjunto.
          </div>
        );
      }
      return (
        <div className="eml-preview-nested">
          <div className="eml-metadata">
            <div>
              <b>De:</b> {nestedEmail.headers.from || '(sin remitente)'}
            </div>
            <div>
              <b>Para:</b> {nestedEmail.headers.to || '(sin destinatario)'}
            </div>
            <div>
              <b>Asunto:</b> {nestedEmail.headers.subject || '(sin asunto)'}
            </div>
            <div>
              <b>Fecha:</b> {nestedEmail.headers.date || '(sin fecha)'}
            </div>
          </div>
          {nestedAttachments.length > 0 && (
            <div className="eml-attachments">
              <div className="eml-attachments-title">
                <FiPaperclip size={18} />
                &nbsp;Adjuntos ({nestedAttachments.length})
              </div>
              {nestedAttachments.map((item, index) => (
                <div className="eml-attachment" key={`${item.filename}-${index}`}>
                  <span className="eml-attachment-name" title={item.contentType}>
                    {item.filename}
                  </span>
                  <span className="eml-attachment-size">{formatSize(item.size)}</span>
                  {showDownload ? (
                    <FiDownload
                      size={20}
                      className="eml-icon"
                      title="descargar adjunto"
                      onClick={() => onDownload(item)}
                    />
                  ) : (
                    <span
                      className="eml-attachment-blocked"
                      title="Usuario No tiene Permisos para Descargar"
                    >
                      sin permiso de descarga
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <iframe
            className="eml-preview-frame"
            title={nestedEmail.headers.subject || attachment.filename}
            sandbox={SANDBOX}
            srcDoc={frameDocument}
          />
        </div>
      );
    }
    return (
      <div className="eml-preview-empty">
        <FiAlertCircle size={28} />
        &nbsp;Este tipo de archivo no se puede previsualizar
        {showDownload ? ', se debe descargar para abrirlo.' : '.'}
      </div>
    );
  };

  return (
    <div className="eml-preview-overlay">
      <div className="eml-preview-bar">
        <span className="eml-preview-name" title={`${attachment.filename} (${attachment.contentType})`}>
          {attachment.filename}
        </span>
        <span className="eml-attachment-size">{formatSize(attachment.size)}</span>
        <div className="eml-preview-actions">
          {kind === 'image' && (
            <>
              <FiZoomOut
                size={22}
                className="eml-icon"
                title="reducir"
                onClick={() => setZoom(Math.max(zoom - ZOOM_STEP, ZOOM_MIN))}
              />
              <span className="eml-preview-zoom">{zoom}%</span>
              <FiZoomIn
                size={22}
                className="eml-icon"
                title="ampliar"
                onClick={() => setZoom(Math.min(zoom + ZOOM_STEP, ZOOM_MAX))}
              />
              <FiMaximize size={22} className="eml-icon" title="ajustar" onClick={() => setZoom(100)} />
            </>
          )}
          {remoteAvailable && !allowRemote && (
            <FiImage
              size={22}
              className="eml-icon"
              title="mostrar contenido remoto"
              onClick={() => setAllowRemote(true)}
            />
          )}
          {showDownload && (
            <FiDownload
              size={22}
              className="eml-icon"
              title="descargar adjunto"
              onClick={() => onDownload(attachment)}
            />
          )}
          <FiX size={24} className="eml-icon" title="cerrar previsualizacion" onClick={onClose} />
        </div>
      </div>
      <div className="eml-preview-body">{renderContent()}</div>
    </div>
  );
}

export default EmlPreview;
